import { useTranslation } from "react-i18next";
import { BookmarkPlus, Check, ExternalLink } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { Modal } from "../../../../shared/ui/modal";
import { Meditation } from "../../domain/types";
import MeditationDetails from "./MeditationDetails";

type Props = {
    meditation: Meditation;
    onClose: () => void;
    onAdd: () => void;
    adding: boolean;
    /** Opens the copy already taken, or the meditation's own page. */
    onNavigate: (id: string) => void;
};

/**
 * Someone else's meditation, read without leaving the catalogue.
 *
 * Choosing among public meditations means reading several, and a full page
 * for each — then back, then the filters again — made comparing them a chore.
 * Here the script and the recording are one click away and the list stays
 * where it was underneath.
 */
export default function MeditationPreview({
    meditation,
    onClose,
    onAdd,
    adding,
    onNavigate,
}: Props) {
    const { t } = useTranslation();

    return (
        <Modal
            isOpen
            onClose={onClose}
            ariaLabel={meditation.title}
            className="max-w-3xl p-4 sm:p-6"
        >
            {/* With a recording, room for the player that playing it brings up. */}
            <div
                className={`flex flex-col gap-4 pt-10 sm:pt-12 ${meditation.has_audio ? "pb-28" : ""}`}
            >
                <MeditationDetails meditation={meditation} titleAs="h2" />

                <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        startIcon={<ExternalLink size={15} />}
                        onClick={() => onNavigate(meditation.id)}
                    >
                        {t("meditation.preview.openPage")}
                    </Button>
                    {meditation.my_copy_id ? (
                        <Button
                            variant="outline"
                            startIcon={<Check size={15} />}
                            onClick={() => onNavigate(meditation.my_copy_id as string)}
                        >
                            {t("meditation.openCopy")}
                        </Button>
                    ) : (
                        <Button
                            startIcon={<BookmarkPlus size={15} />}
                            loading={adding}
                            onClick={onAdd}
                        >
                            {t("meditation.copyToLibrary")}
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
